/**
 * Chamber of Trial: monolith defense systems and challenge types.
 *
 * `src/data/trial-defense.json` is a faithful mirror of the game's I2
 * localization terms, extracted from `Assets/Resources/I2Languages.asset` in
 * the Epic-Conquest-2 repo. Regenerate it when the I2 sheet changes; nothing
 * else needs to change.
 *
 * Two things are deliberately NOT done in the JSON, so it stays a mirror:
 *
 *   1. Placeholders stay as `{0}`. The game substitutes at render time and so
 *      do we — see `monolithDescription`.
 *   2. NGUI colour markup (`[ffca69]` opens, `[-]` closes) stays intact.
 *      `stripMarkup` / `parseMarkup` handle it at the UI boundary.
 *
 * `monolithDescription` mirrors `EC2Utils.GetDefenseSystem` plus the
 * activation-cooldown line that `TrialUI.SetStageInfo` appends. Keep them in
 * step: what an author reads here must be what a player reads in game.
 */

import data from "@/data/trial-defense.json";

export interface LocalizedTerm {
  term: string;
  en: string;
  id: string;
}

export interface MonolithEffect extends LocalizedTerm {
  /** Monolith_Attack enum value. Serialized as this int in trial JSON. */
  value: number;
  /** Enum member name, e.g. "MpBurn". The stable identifier. */
  name: string;
}

export interface ChallengeTypeTerm extends LocalizedTerm {
  /** TrialChallengeType enum value. */
  value: number;
  name: string;
}

export type TermLanguage = "en" | "id";

export const MONOLITH_EFFECTS: MonolithEffect[] = data.monolithEffects;
export const CHALLENGE_TYPES: ChallengeTypeTerm[] = data.challengeTypes;
export const ACTIVATION_TIME_TEMPLATE: LocalizedTerm = data.activationTimeTemplate;

/** Highest legal Monolith_Attack value. Used by the trial schema validator. */
export const MONOLITH_MAX_VALUE = MONOLITH_EFFECTS.length - 1;
/** Highest legal TrialChallengeType value. */
export const CHALLENGE_TYPE_MAX_VALUE = CHALLENGE_TYPES.length - 1;

const effectByValue = new Map(MONOLITH_EFFECTS.map((e) => [e.value, e]));
const challengeByValue = new Map(CHALLENGE_TYPES.map((c) => [c.value, c]));

export function monolithEffect(value: number): MonolithEffect | undefined {
  return effectByValue.get(value);
}

export function isValidMonolithValue(value: number): boolean {
  return effectByValue.has(value);
}

export function isValidChallengeType(value: number): boolean {
  return challengeByValue.has(value);
}

/** Enum member name, or a visible marker so an unknown value never renders blank. */
export function monolithName(value: number): string {
  return effectByValue.get(value)?.name ?? `Unknown (${value})`;
}

export function challengeTypeName(value: number): string {
  return challengeByValue.get(value)?.name ?? `Unknown (${value})`;
}

/** The subset of MonolithValues that affects the rendered description. */
export interface MonolithValuesLike {
  attackPotency: number;
  activationTime: number;
}

/**
 * The description a player sees for this defense system, with markup intact.
 *
 * Mirrors `EC2Utils.GetDefenseSystem`: `attackPotency` fills `{0}`, but only
 * when it is greater than zero. When it is zero the game's `string.Format`
 * throws on the missing argument and its catch block returns the raw term —
 * so `{0}` stays visible in game, and it stays visible here too. That is not
 * a bug to paper over: a potency-based effect left at 0 is misconfigured, and
 * the author should see exactly what the player would.
 *
 * Then, matching `TrialUI`, an activation-cooldown line is appended whenever
 * `activationTime` is greater than zero.
 */
export function monolithDescription(
  value: number,
  values: MonolithValuesLike,
  lang: TermLanguage = "en"
): string {
  const effect = effectByValue.get(value);
  if (!effect) return `Unknown defense system (${value})`;

  let result = effect[lang] || effect.en;
  if (values.attackPotency > 0) {
    result = result.replace("{0}", String(values.attackPotency));
  }

  if (values.activationTime > 0) {
    const template = ACTIVATION_TIME_TEMPLATE[lang] || ACTIVATION_TIME_TEMPLATE.en;
    result += "\n" + template.replace("{0}", String(values.activationTime));
  }

  return result;
}

/**
 * The description for one challenge row. `amount` only appears for
 * LimitedConsumable (2) and ClearWithin (3) — the other two ignore it, the
 * same way `TrialChallenge`'s `[ShowIf]` hides the field in the game's own
 * inspector.
 */
export function challengeDescription(
  value: number,
  amount: number,
  lang: TermLanguage = "en"
): string {
  const challenge = challengeByValue.get(value);
  if (!challenge) return `Unknown challenge (${value})`;
  const text = challenge[lang] || challenge.en;
  return text.replace("{0}", String(amount));
}

/** True when this effect's description has a `{0}` that `attackPotency` fills. */
export function usesAttackPotency(value: number): boolean {
  return effectByValue.get(value)?.en.includes("{0}") ?? false;
}

// NGUI `[ffca69]…[-]` and Unity rich-text `<color=#ffca69>…</color>` (hex or a named colour).
const MARKUP = /\[(?:-|[0-9a-fA-F]{6})\]|<color=["']?(?:#[0-9a-fA-F]{3,8}|[a-zA-Z]+)["']?>|<\/color>/g;

/** Plain text, for anywhere that cannot render spans (titles, list rows, diffs). */
export function stripMarkup(text: string): string {
  return text.replace(MARKUP, "");
}

export interface MarkupSegment {
  text: string;
  /** A CSS colour (`#ffca69`, `red`). Undefined means default colour. */
  color?: string;
}

/** The colour an opening tag sets, or undefined for a closing tag. */
function tagColor(tag: string): string | undefined {
  if (tag.startsWith("[")) return tag === "[-]" ? undefined : `#${tag.slice(1, -1)}`;
  if (tag.startsWith("</")) return undefined;
  return tag.slice("<color=".length, -1).replace(/["']/g, "");
}

/**
 * Split colour markup into renderable segments.
 *
 * `[ffca69]highlighted[-]` and `<color=#ffca69>highlighted</color>` both become
 * a coloured middle segment. Tags nest; a closing tag (`[-]` or `</color>`)
 * returns to the enclosing colour. Unclosed tags run to the end of the string,
 * which is what NGUI and Unity do.
 */
export function parseMarkup(text: string): MarkupSegment[] {
  const segments: MarkupSegment[] = [];
  const stack: string[] = [];
  let cursor = 0;

  for (const match of text.matchAll(MARKUP)) {
    const at = match.index ?? 0;
    if (at > cursor) segments.push({ text: text.slice(cursor, at), color: stack.at(-1) });
    const color = tagColor(match[0]);
    if (color) stack.push(color); else stack.pop();
    cursor = at + match[0].length;
  }
  if (cursor < text.length) segments.push({ text: text.slice(cursor), color: stack.at(-1) });

  return segments;
}
